/** @jsx jsx */
import { useContext } from 'react';
import { Link } from 'react-router-dom';

import { css, jsx, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonPrimary } from './ButtonPrimary';
import { ButtonSecondary } from './ButtonSecondary';
import { ButtonDanger } from './ButtonDanger';
import { ButtonOrange } from './ButtonOrange';
import { ButtonSimple } from './ButtonSimple';
import { ButtonBoxed } from './ButtonBoxed';
import { ButtonBoxedGhost } from './ButtonBoxedGhost';
import { ButtonFormueBoxed } from './ButtonFormueBoxed';
import { themeStyles } from '../../services/themeStyles';
import { colors } from '../../global';
import { CoreContext } from '../../providers';
import { media } from '../../services/media';
import { classNames } from '../../services/classNames';

const Formue = themeStyles(
    'formue',
    css`
        ${media.max('xl')} {
            border-radius: 50px;
        }

        ${media.min('xl')} {
            border-radius: 100px;
        }
    `,
);

const Upscore = themeStyles(
    `upscore`,
    css`
        ${media.max('xl')} {
            border-radius: 50px;
        }

        ${media.min('xl')} {
            border-radius: 100px;
        }
    `,
);

const BaseStyles = css`
    display: inline-block;

    ${media.max('md')} {
        min-width: 130px;
        padding: 15px 24px;
    }

    ${media.min('md')} {
        padding: 20px 32px;
        min-width: 210px;
    }

    font-weight: 400;
    line-height: 1;
    text-align: center;
    text-decoration: none;

    border: none;
    background-color: ${colors.white};
    cursor: pointer;

    &:disabled {
        cursor: no-drop;
        opacity: 0.8;
    }

    &:focus,
    &:hover {
        outline: none;
    }

    &[type='submit'] {
        line-height: 1;
        margin: 0 auto;
    }

    ${media.min('lg')} {
        min-width: 260px;
    }
`;

const buttonTypes = {
    external: ({ to, children, ...rest }: any) => (
        <a href={to} rel="noreferer noopener" {...rest}>
            {children}
        </a>
    ),
    local: Link,
    button: (props: any) => <button type="button" {...props} />,
    formSubmit: ({ children, ...rest }: any) => (
        <input type="submit" value={children} {...rest} />
    ),
};

const getButtonTypeByProps = ({ to, external, formSubmit, onClick }: any) => {
    if (onClick) {
        return 'button';
    }

    if (external) {
        return 'external';
    }

    if (to) {
        return 'local';
    }

    if (formSubmit) {
        return 'formSubmit';
    }

    return 'button';
};

interface ButtonProps {
    variant:
        | 'primary'
        | 'secondary'
        | 'simple'
        | 'danger'
        | 'orange'
        | 'boxed'
        | 'formue-boxed'
        | 'boxed-ghost'
        | 'greenGradient'
        | 'boxedElephant'
        | 'whiteBlue'
        | 'whiteTuruoise'
        | 'boxedRounded'
        | 'boxedGhost'
        | 'roundedBahamaBlue';
    className?: string;
    external?: boolean;
    formSubmit?: boolean;
    to?: string;
    onClick?: () => void;
    type?: string;
    disabled?: boolean;
}

const ButtonVariants: Record<ButtonProps['variant'], SerializedStyles> = {
    primary: css``,
    'boxed-ghost': css``,
    'formue-boxed': css``,
    boxed: css``,
    danger: css``,
    orange: css``,
    secondary: css``,
    simple: css``,
    greenGradient: css`
        background: ${colors.turquoiseGreenGradient};
        color: ${colors.white};
        border-radius: 100px;
    `,
    boxedElephant: css`
        background-color: ${colors.white};
        color: ${colors.elephant};
        border: 3px solid ${colors.elephant};
        font-size: 20px;

        &:hover {
            background-color: ${colors.elephant};
            color: ${colors.white};
            text-decoration: underline;
        }
    `,
    whiteBlue: css`
        color: ${colors.bahamaBlue};
        background-color: ${colors.white};
        border-radius: 10px;
    `,
    whiteTuruoise: css`
        color: ${colors.turquoiseBlue};
        background-color: ${colors.white};
        border-radius: 100px;
    `,
    boxedRounded: css`
        border-radius: 100px;
        background-color: transparent;
        border: 2px solid ${colors.white};
        color: ${colors.white};
    `,
    boxedGhost: css`
        background-color: ${colors.elephant};
        border: 3px solid ${colors.white};
        color: ${colors.white};

        &:hover {
            text-decoration: underline;
            background-color: ${colors.white};
            color: ${colors.elephant};
        }
    `,
    roundedBahamaBlue: css`
        background: #37abf4;
        color: #ffffff;
        border-radius: 50px;
        text-decoration: none;
        font-size: inherit;
    `,
};

export const Button: React.FC<ButtonProps> = ({
    className,
    variant,
    external,
    formSubmit,
    ...rest
}) => {
    const { settings } = useContext(CoreContext);
    const buttonType = getButtonTypeByProps({
        to: rest.to,
        external,
        formSubmit,
    });
    const TargetComponent = buttonTypes[buttonType];

    const StyledButton = styled(TargetComponent, {
        shouldForwardProp: () => true,
    })`
        ${BaseStyles}

        ${Upscore}
        ${Formue}

        font-family: ${(props) => props.theme.headings.fontFamily};

        ${variant === 'primary' && ButtonPrimary}
        ${variant === 'secondary' && ButtonSecondary}
        ${variant === 'danger' && ButtonDanger}
        ${variant === 'orange' && ButtonOrange}
        ${variant === 'simple' && ButtonSimple}
        ${variant === 'boxed' && ButtonBoxed}
        ${variant === 'boxed-ghost' && ButtonBoxedGhost}
        ${variant === 'formue-boxed' && ButtonFormueBoxed}

        ${ButtonVariants[variant]}
    `;

    return (
        <StyledButton
            className={classNames(
                'Button',
                settings.languageVersion,
                className || '',
            )}
            {...rest}
        />
    );
};
